import { apiFetch } from "../../apiClient";
import { checkAuthStatus } from "../../utils/authStatus";
import { parseErrorResponse } from "../../utils/apiError";

export const YOUR_FILES_WEB_PATH = "/your-files";
export const SKILLS_WEB_PATH = "/skills";

function buildFinsterWebAppUrl(
  webPath: string,
  baseUrl?: string,
): string | undefined {
  const normalizedBaseUrl = baseUrl?.trim();
  if (!normalizedBaseUrl) return undefined;

  try {
    const baseWithTrailingSlash = normalizedBaseUrl.endsWith("/")
      ? normalizedBaseUrl
      : `${normalizedBaseUrl}/`;
    const url = new URL(webPath.replace(/^\//, ""), baseWithTrailingSlash);
    if (url.protocol !== "https:" && url.protocol !== "http:") {
      return undefined;
    }
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return undefined;
  }
}

export interface UserFile {
  id: string;
  name: string;
  status: string;
  createdAt: string;
  updatedAt?: string;
  path?: string;
  summary?: string;
  tags: string[];
  companyNames: string[];
  industry: string[];
}

export class UserFilesApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "UserFilesApiError";
    this.status = status;
  }
}

export function isQueryableUserFileStatus(status: string): boolean {
  const normalized = status.trim().toLowerCase();
  return normalized === "uploaded" || normalized === "ready";
}

/**
 * Builds the outbound "manage Your Files" URL from a configured web-app base.
 *
 * The destination UI depends on `FINSTER_WEB_URL`: standalone Finster locally,
 * and the matching tenant Finster web-app host in deployed environments
 * (development uses st4 to match the backend). See
 * `FinsterAddIn/DEPLOYMENT.md` for the matrix.
 */
export function buildYourFilesManagementUrl(baseUrl?: string): string | undefined {
  return buildFinsterWebAppUrl(YOUR_FILES_WEB_PATH, baseUrl);
}

/**
 * Builds the outbound "manage Skills" URL from a configured web-app base.
 *
 * Local uses standalone Finster. Deployed environments use the matching tenant
 * Finster web-app host (development and FactSet production: st4; staging:
 * st4-uat; FDS UBS: fds-prod-ubs). See `FinsterAddIn/DEPLOYMENT.md`.
 */
export function buildSkillsManagementUrl(baseUrl?: string): string | undefined {
  return buildFinsterWebAppUrl(SKILLS_WEB_PATH, baseUrl);
}

export async function getUserFiles(signal?: AbortSignal): Promise<UserFile[]> {
  const response = await apiFetch("/api/finster/user-files", {
    method: "GET",
    signal,
  });

  checkAuthStatus(response);
  if (!response.ok) {
    throw new UserFilesApiError(response.status, await parseErrorResponse(response));
  }

  const payload = (await response.json()) as unknown;
  if (!Array.isArray(payload)) {
    throw new UserFilesApiError(502, "Your Files returned an invalid response.");
  }

  const isValidPayload = payload.every(
    (item): item is UserFile =>
      Boolean(
        item &&
          typeof item === "object" &&
          typeof (item as UserFile).id === "string" &&
          typeof (item as UserFile).name === "string" &&
          typeof (item as UserFile).status === "string" &&
          typeof (item as UserFile).createdAt === "string" &&
          Array.isArray((item as UserFile).tags) &&
          Array.isArray((item as UserFile).companyNames) &&
          Array.isArray((item as UserFile).industry)
      )
  );
  if (!isValidPayload) {
    throw new UserFilesApiError(502, "Your Files returned an invalid file record.");
  }
  return payload as UserFile[];
}
